/**
 * Headless layout service for the related-tags feature.
 *
 * Calculates how many tag items fit in a container row before overflow,
 * taking into account the width of the toggle badge.
 * Pure function — no direct DOM mutations, fully testable.
 */
/**
 * Calculate how many tags fit within `containerWidth`,
 * reserving space for the toggle badge when there are hidden items.
 *
 * @param containerWidth  Pixel width of the outer container.
 * @param itemWidths      Array of per-item pixel widths (in display order).
 * @param toggleWidth     Pixel width of the "+N more" toggle badge.
 * @param options         Optional configuration (gap, minVisibleWhenCollapsed).
 */
export function calculateVisibleCount(containerWidth, itemWidths, toggleWidth, options = {}) {
    var _a, _b;
    const gap = (_a = options.gap) !== null && _a !== void 0 ? _a : 6;
    const minVisibleWhenCollapsed = (_b = options.minVisibleWhenCollapsed) !== null && _b !== void 0 ? _b : 0;
    if (itemWidths.length === 0) {
        return { visibleCount: 0, hiddenCount: 0 };
    }
    let usedWidth = 0;
    let visibleCount = 0;
    for (let i = 0; i < itemWidths.length; i++) {
        const itemWidth = itemWidths[i] + (i > 0 ? gap : 0);
        const wouldNeedToggle = i < itemWidths.length - 1; // more items after this one
        const widthWithToggle = usedWidth + itemWidth + (wouldNeedToggle ? gap + toggleWidth : 0);
        if (widthWithToggle <= containerWidth) {
            usedWidth += itemWidth;
            visibleCount++;
        }
        else {
            break;
        }
    }
    // Apply minVisibleWhenCollapsed policy: ensure at least one tag is visible in collapsed state
    if (minVisibleWhenCollapsed > 0 && itemWidths.length > 0) {
        visibleCount = Math.max(minVisibleWhenCollapsed, visibleCount);
    }
    // Clamp visibleCount to item count
    visibleCount = Math.min(visibleCount, itemWidths.length);
    const hiddenCount = itemWidths.length - visibleCount;
    return { visibleCount, hiddenCount };
}
/**
 * Read the pixel widths of all child elements matching `selector` inside `root`.
 */
export function measureItemWidths(root, selector) {
    const els = Array.from(root.querySelectorAll(selector));
    return els.map((el) => el.offsetWidth);
}
